import { useEffect, useCallback, useRef } from 'react';
import { TreeNode, TreeIndexMap } from '../types';
import { buildTreeIndex } from '../utils';

type UseNewFileDetectionParams = {
  files: Record<string, string>;
  tree: TreeNode[];
  setTree: (tree: TreeNode[]) => void;
  setTreeIndex: (index: TreeIndexMap) => void;
  onNewFile?: (path: string) => void;
  onDeletedFile?: (path: string) => void;
};

// Build tree structure from file paths
function buildTreeFromPaths(paths: string[]): TreeNode[] {
  const root: TreeNode[] = [];
  const nodeMap = new Map<string, TreeNode>();

  // Sort paths to ensure parent directories are created first
  const sortedPaths = [...paths].sort();

  for (const fullPath of sortedPaths) {
    const parts = fullPath.split('/').filter(Boolean);
    let currentPath = '';
    let currentLevel = root;

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      currentPath = currentPath ? `${currentPath}/${part}` : part;
      const isFile = i === parts.length - 1;

      let node = nodeMap.get(currentPath);
      if (!node) {
        node = {
          id: currentPath,
          name: part,
          path: currentPath,
          type: isFile ? 'file' : 'folder',
          children: isFile ? undefined : [],
        };
        nodeMap.set(currentPath, node);
        currentLevel.push(node);
      }

      if (!isFile && node.children) {
        currentLevel = node.children;
      }
    }
  }

  return root;
}

// Check if a path exists in the tree
function pathExistsInTree(tree: TreeNode[], path: string): boolean {
  for (const node of tree) {
    if (node.path === path || node.id === path) {
      return true;
    }
    if (node.children && pathExistsInTree(node.children, path)) {
      return true;
    }
  }
  return false;
}

// Add a new node to the tree
function addNodeToTree(tree: TreeNode[], filePath: string): TreeNode[] {
  const parts = filePath.split('/').filter(Boolean);
  const newTree = JSON.parse(JSON.stringify(tree)) as TreeNode[];

  let currentLevel = newTree;
  let currentPath = '';

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    currentPath = currentPath ? `${currentPath}/${part}` : part;
    const isFile = i === parts.length - 1;

    let existingNode = currentLevel.find((n) => n.name === part);

    if (!existingNode) {
      existingNode = {
        id: currentPath,
        name: part,
        path: currentPath,
        type: isFile ? 'file' : 'folder',
        children: isFile ? undefined : [],
      };
      currentLevel.push(existingNode);
      // Sort to maintain alphabetical order
      currentLevel.sort((a, b) => {
        // Directories first, then files
        if (a.type !== b.type) {
          return a.type === 'folder' ? -1 : 1;
        }
        return a.name.localeCompare(b.name);
      });
    }

    if (!isFile && existingNode.children) {
      currentLevel = existingNode.children;
    }
  }

  return newTree;
}

// Remove a node from the tree
function removeNodeFromTree(tree: TreeNode[], filePath: string): TreeNode[] {
  const newTree: TreeNode[] = [];

  for (const node of tree) {
    if (node.path === filePath || node.id === filePath) {
      continue; // Skip this node
    }

    if (node.children) {
      const updatedChildren = removeNodeFromTree(node.children, filePath);
      newTree.push({
        ...node,
        children: updatedChildren,
      });
    } else {
      newTree.push({ ...node });
    }
  }

  return newTree;
}

export function useNewFileDetection({
  files,
  tree,
  setTree,
  setTreeIndex,
  onNewFile,
  onDeletedFile,
}: UseNewFileDetectionParams) {
  const previousFilePaths = useRef<Set<string>>(new Set());

  // Detect new and deleted files
  const detectChanges = useCallback(() => {
    const currentPaths = new Set(Object.keys(files));
    const previousPaths = previousFilePaths.current;

    const newFiles: string[] = [];
    const deletedFiles: string[] = [];

    // Find new files
    currentPaths.forEach((path) => {
      if (!previousPaths.has(path)) {
        newFiles.push(path);
      }
    });

    // Find deleted files
    previousPaths.forEach((path) => {
      if (!currentPaths.has(path)) {
        deletedFiles.push(path);
      }
    });

    // Update tree for new files
    if (newFiles.length > 0 || deletedFiles.length > 0) {
      let updatedTree = [...tree];

      for (const path of newFiles) {
        if (!pathExistsInTree(updatedTree, path)) {
          updatedTree = addNodeToTree(updatedTree, path);
          onNewFile?.(path);
        }
      }

      for (const path of deletedFiles) {
        updatedTree = removeNodeFromTree(updatedTree, path);
        onDeletedFile?.(path);
      }

      if (newFiles.length > 0 || deletedFiles.length > 0) {
        setTree(updatedTree);
        setTreeIndex(buildTreeIndex(updatedTree));
      }
    }

    // Update previous paths
    previousFilePaths.current = currentPaths;
  }, [files, tree, setTree, setTreeIndex, onNewFile, onDeletedFile]);

  // Run detection on files change
  useEffect(() => {
    detectChanges();
  }, [detectChanges]);

  // Initialize previous paths on mount
  useEffect(() => {
    previousFilePaths.current = new Set(Object.keys(files));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Build tree from files (useful for initial load or full rebuild)
  const rebuildTreeFromFiles = useCallback(() => {
    const paths = Object.keys(files);
    const newTree = buildTreeFromPaths(paths);
    setTree(newTree);
    setTreeIndex(buildTreeIndex(newTree));
  }, [files, setTree, setTreeIndex]);

  return {
    detectChanges,
    rebuildTreeFromFiles,
  };
}

export { buildTreeFromPaths, addNodeToTree, removeNodeFromTree, pathExistsInTree };
